import * as _ from 'underscore';
import { IListFetcher } from '../../../ts/fetchers/IListFetcher';
import { ListResult } from '../../../ts/fetchers/ListResult';
import { Filter } from '../../../ts/helpers/Filter';
import { RequestHelper } from '../../../ts/helpers/RequestHelper';
import { IImportUsersResult, ResultHelper } from '../../../ts/helpers/ResultHelper';
import { ServerApiMapper } from '../../../ts/helpers/ServerModelHelper';
import { SliceableHelper } from '../../../ts/helpers/SliceableHelper';
import { ISiteUserInfo } from '../../../ts/models/ISiteUserInfo';
import { Module } from '../../../ts/Module';
import { AddUsersToSiteRequest } from '../../../ts/ServerApi/requests/AddUsersToSiteRequest';
import { CreateLocalUserRequest } from '../../../ts/ServerApi/requests/CreateLocalUserRequest';
import { CreateLocalUserWithSiteSamlAuthRequest } from '../../../ts/ServerApi/requests/CreateLocalUserWithSiteSamlAuthRequest';
import { DeleteUsersRequest } from '../../../ts/ServerApi/requests/DeleteUsersRequest';
import { GeneratePublicKeyRequest, IGeneratePublicKeyResult } from '../../../ts/ServerApi/requests/GeneratePublicKeyRequest';
import {
  GetRemainingUserQuotaRequest,
  IGetRemainingUserQuotaResult,
} from '../../../ts/ServerApi/requests/GetRemainingUserQuotaRequest';
import { GetSiteUsersRequest } from '../../../ts/ServerApi/requests/GetSiteUsersRequest';
import { ImportExternalSiteUsersRequest } from '../../../ts/ServerApi/requests/ImportExternalUsers';
import { ImportSiteUsersFromActiveDirectoryRequest } from '../../../ts/ServerApi/requests/ImportUsersFromActiveDirectoryRequest';
import { UpdateUsersAdminStatusRequest } from '../../../ts/ServerApi/requests/UpdateUsersAdminStatusRequest';
import { UpdateUsersGroupMembershipRequest } from '../../../ts/ServerApi/requests/UpdateUsersGroupMembershipRequest';
import { UpdateUsersSiteRoleRequest } from '../../../ts/ServerApi/requests/UpdateUsersSiteRoleRequest';
import { FilterFieldNames, IFieldFilterClause, IFilterClause } from '../../../ts/ServerApi/types/IFilterClause';
import { IPagedItemsParams } from '../../../ts/ServerApi/types/IPage';
import { IResult } from '../../../ts/ServerApi/types/IResponse';
import { SiteRoles } from '../../../ts/ServerApi/types/ISiteRole';
import { IUserActionResult } from '../../../ts/ServerApi/types/IUserActionResult';
import { ErrorNotifyingServer } from '../../../ts/services/ErrorNotifyingServer';
import { FetcherFactory } from '../../../ts/services/FetcherFactory';
import { ServerService } from '../../../ts/services/ServerService';
import {
  ISiteAuthenticationSettings,
  SiteAuthenticationSettingsService,
} from '../../../ts/services/SiteAuthenticationSettingsService';
import { Resource } from '../../../ts/resources/Resource';
import { IResourceQuery } from '../../../ts/resources/ResourceQuery';
import { SiteRolesResource } from './SiteRoles';
import { ServiceLocator } from '../../../app/ServiceLocator';


function fetchList(params: IPagedItemsParams): ng.IPromise<ListResult<ISiteUserInfo>> {
  let siteAuthenticationSettingsService: SiteAuthenticationSettingsService = ServiceLocator.get<SiteAuthenticationSettingsService>('SiteAuthenticationSettingsService');
  let fetcherFactory: FetcherFactory = ServiceLocator.get<FetcherFactory>('FetcherFactory');
  let siteUserFetcher: IListFetcher<ISiteUserInfo> = fetcherFactory.getServerResourceFetcher(GetSiteUsersRequest, ServerApiMapper.siteUsers);

  if (!siteAuthenticationSettingsService.isEnabled()) {
    return siteUserFetcher.fetchList(params);
  }

  let $q: ng.IQService = ServiceLocator.get<ng.IQService>('$q');
  return $q.all([
    siteUserFetcher.fetchList(params),
    siteAuthenticationSettingsService.getAuthSettingToProviderIdMap()])
  .then((results: any[]) => {
    const userList: ListResult<ISiteUserInfo> = results[0];
    const map: _.Dictionary<string> = results[1];

    _.each(userList.items, (siteUser: ISiteUserInfo) => {
      siteUser.authenticationProviderId = map[siteUser.authSetting];
    });
    return userList;
  });
}

export const AuthenticationProviderIdFetcher = {fetchList};

export class SiteUsers extends Resource<ISiteUserInfo>{
  public static readonly instance = new SiteUsers(AuthenticationProviderIdFetcher);

  // gets the user with the specified username & domainName.
  public getUserByUsernameAndDomain(username: string, domainName: string): ng.IPromise<any> {
    return this.all()
      .addFilter(Filter.stringFieldEq('username', username))
      .addFilter(Filter.stringFieldEq('domainName', domainName))
      .getFirst();
  }

  public getUsersById(userIds: string[]): ng.IPromise<ListResult<ISiteUserInfo>> {
    return SliceableHelper.fetchAll(this.all()
      .addFilter(Filter.stringFieldInArray('id', userIds)));
  }

  // gets users with the specified siteRoles.
  public getUsersBySiteRoles(siteRoles: string[]): ng.IPromise<ListResult<ISiteUserInfo>> {
    return SliceableHelper.fetchAll(this.all()
      .addFilter(Filter.stringFieldInArray(FilterFieldNames.siteRole, siteRoles)));
  }

  // group membership - remove only
  public removeFromGroups(userIds: string[], groupIds: string[]): ng.IPromise<IResult> {
    return this.getErrorNotifyServerService().sendRequestsAndDelay(
      RequestHelper.buildRequests2(userIds, groupIds,
        (userIdsBlock, groupIdsBlock) => new UpdateUsersGroupMembershipRequest({
          userIds: userIdsBlock,
          addToGroupIds: [],
          removeFromGroupIds: groupIdsBlock
        })),
      ResultHelper.combineResults);
  }

  public getUserCountsBySiteRole(baseQuery: IResourceQuery<ISiteUserInfo>): ng.IPromise<_.Dictionary<number>> {

    // remove any existing site role filter
    const filtersWithoutSiteRole = _.filter(baseQuery.getFilters(), (filter: IFieldFilterClause) => {
      return !(filter.field && filter.field === FilterFieldNames.siteRole);
    });
    const modifiedQuery = baseQuery.withFilters(filtersWithoutSiteRole);

    const counts: _.Dictionary<number> = {};

    const allSiteRolesExceptGuest = _.without(SiteRolesResource.instance.allOptions(), SiteRoles.Guest);

    const promises = _.map(allSiteRolesExceptGuest, (siteRole: string) => {
      return modifiedQuery
        .addFilter(Filter.stringFieldEq(FilterFieldNames.siteRole, siteRole))
        .getCount()
        .then((totalCount: number) => {
          counts[siteRole] = totalCount;
        });
    });

    //Any site role
    promises.push(modifiedQuery.getCount().then((totalCount: number) => {counts['Any'] = totalCount; }));

    let $q: ng.IQService = ServiceLocator.get<ng.IQService>('$q');
    return $q.all(promises).then(() => counts);
  }

  // group membership - add only
  public addToGroups(userIds: string[], groupIds: string[]): ng.IPromise<IResult> {
    return this.getErrorNotifyServerService().sendRequestsAndDelay(
      RequestHelper.buildRequests2(userIds, groupIds,
        (userIdsBlock, groupIdsBlock) => new UpdateUsersGroupMembershipRequest({
          userIds: userIdsBlock,
          addToGroupIds: groupIdsBlock,
          removeFromGroupIds: []
        })),
      ResultHelper.combineResults);
  }

  // group membership - add and remove
  public updateUsersGroupMembership(userIds: string[], addToGroupIds: string[], removeFromGroupIds: string[]): ng.IPromise<IResult> {
    return this.getErrorNotifyServerService().sendRequestsAndDelay(
      RequestHelper.buildRequests3(userIds, addToGroupIds, removeFromGroupIds,
        (userIdsBlock, addToGroupIdsBlock, removeFromGroupIdsBlock) => new UpdateUsersGroupMembershipRequest({
          userIds: userIdsBlock,
          addToGroupIds: addToGroupIdsBlock,
          removeFromGroupIds: removeFromGroupIdsBlock
        })),
      ResultHelper.combineResults);
  }

  // creates a local user
  public createLocalUser(
    username: string,
    displayName: string,
    email: string,
    password: string,
    admin: boolean,
    siteRole: string
  ): ng.IPromise<IUserActionResult> {
    return this.getServerService().sendRequest(new GeneratePublicKeyRequest()).
      then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
        const key = generatePublicKeyResult.key;
        const keyId = generatePublicKeyResult.keyId;
        const encryptedPassword = rsa.encrypt(password, key);
        const createLocalUserRequest = new CreateLocalUserRequest({
          username: username,
          displayName: displayName,
          email: email,
          encryptedPassword: encryptedPassword,
          keyId: keyId,
          admin: admin,
          siteRole: siteRole
        });
        return this.getErrorNotifyServerService().sendRequestAndDelay(createLocalUserRequest);
      });
  }

  // creates a local SiteSAML user
  public createLocalUserWithSamlOrSiteSamlAuth(
    username: string,
    displayName: string,
    email: string,
    authenticationProviderId: string,
    admin: boolean,
    siteRole: string
  ): ng.IPromise<IUserActionResult> {
    return this.getSiteAuthenticationSettingsService().getSiteAuthenticationSettings().then((settings: ISiteAuthenticationSettings) => {
      const authSetting = this.getSiteAuthenticationSettingsService().getAuthSetting(authenticationProviderId, settings);
      const createLocalUserWithSiteSamlAuthRequest = new CreateLocalUserWithSiteSamlAuthRequest({
            username: username,
            displayName: displayName,
            email: email,
            authSetting: authSetting,
            admin: admin,
            siteRole: siteRole
          });
      return this.getErrorNotifyServerService().sendRequestAndDelay(createLocalUserWithSiteSamlAuthRequest);
    });
  }

  // adds users to this site
  public addUsersToSite(usernames: string[], siteRole: string): ng.IPromise<IResult> {
    return this.getErrorNotifyServerService().sendRequestsAndDelay(
      RequestHelper.buildRequests(usernames, (usernamesBlock) => new AddUsersToSiteRequest(usernamesBlock, siteRole)),
      ResultHelper.combineResults);
  }

  // delete users from this site
  public deleteUsers(userIds: string[]): ng.IPromise<IUserActionResult> {
    return this.getErrorNotifyServerService().sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new DeleteUsersRequest(userIdsBlock)),
      ResultHelper.combineUserResults);
  }

  // imports users from active directory to this site
  public importUsersFromActiveDirectory(userIds: string[], siteRole: string, serverAdmin: boolean = false): ng.IPromise<IUserActionResult> {
    let requestBuilder: (userIds: string[]) => ImportSiteUsersFromActiveDirectoryRequest;
    if (serverAdmin) {
      requestBuilder = (userIdsBlock) => new ImportSiteUsersFromActiveDirectoryRequest({ usernames: userIdsBlock, admin: true });
    } else {
      requestBuilder = (userIdsBlock) => new ImportSiteUsersFromActiveDirectoryRequest({ usernames: userIdsBlock, siteRole: siteRole });
    }
    return this.getErrorNotifyServerService().sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, requestBuilder),
      ResultHelper.combineUserResults);
  }

  public importExternalUsers(emails: string[], siteRole: string, authenticationProviderId: string): ng.IPromise<IUserActionResult> {
    return this.getSiteAuthenticationSettingsService().getSiteAuthenticationSettings().then((settings: ISiteAuthenticationSettings) => {
      const authSetting = this.getSiteAuthenticationSettingsService().getAuthSetting(authenticationProviderId, settings);
      return this.getErrorNotifyServerService().sendRequestsAndDelay(
        RequestHelper.buildRequests(emails, (emailsBlock) => new ImportExternalSiteUsersRequest({
          emailAddresses: emailsBlock,
          siteRole: siteRole,
          authSetting: authSetting
        })),
        ResultHelper.combineUserResults);
    });
  }

  // updates users site role for this site
  public updateUsersSiteRole(userIds: string[], siteRole: string): ng.IPromise<IResult> {
    return this.getErrorNotifyServerService().sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new UpdateUsersSiteRoleRequest({
        userIds: userIdsBlock,
        siteRole: siteRole
      })),
      ResultHelper.combineResults);
  }

  // site membership - change these users' server admin status. Includes SOLR delay.
  public updateServerAdminStatusAndDelay(userIds: string[], admin: boolean): ng.IPromise<IResult> {
    return this.getErrorNotifyServerService().sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new UpdateUsersAdminStatusRequest({
        userIds: userIdsBlock,
        admin: admin,
      })),
      ResultHelper.combineResults);
  }

  // site membership - change these users' server admin status. No SOLR delay.
  public updateServerAdminStatusNoDelay(userIds: string[], admin: boolean): ng.IPromise<IResult> {
    return this.getErrorNotifyServerService().sendRequests(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new UpdateUsersAdminStatusRequest({
        userIds: userIdsBlock,
        admin: admin,
      })),
      ResultHelper.combineResults);
  }

  public getRemainingUserQuota(): ng.IPromise<number> {
    return this.getServerService().sendRequest(new GetRemainingUserQuotaRequest())
      .then((result: IGetRemainingUserQuotaResult) => result.usersRemaining);
  }

  private getServerService(): ServerService {
    return ServiceLocator.get<ServerService>('ServerService');
  }

  private getErrorNotifyServerService(): ErrorNotifyingServer {
    return ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
  }

  private getSiteAuthenticationSettingsService(): SiteAuthenticationSettingsService {
    return ServiceLocator.get<SiteAuthenticationSettingsService>('SiteAuthenticationSettingsService');
  }
}

export default SiteUsers.instance;
